import { useMemo, type CSSProperties } from "react";

import { getOffsetOriginConfig } from "@skilitics/drag-drop";

import type { DesignerNode } from "../types/index.ts";
import type { ElementStateKey, StylesByElementState } from "./element-state/index.ts";
import { useDetailViewElementStateKey, type ElementStateConfig } from "./element-state/index.ts";
import { combineCssWithOverrides, transformLayoutSettingsToCss } from "./properties/index.ts";
import type { LayoutSettings, Orientation } from "./types.ts";

type CSSVariables = Record<string, string | number>;

export type GenerateDetailViewCssParams = {
  /** The base layout settings from the node config */
  baseLayoutSettings: LayoutSettings;
  /** The base CSS properties from the node config */
  baseCssProperties?: Partial<CSSProperties>;
  /** The styles by element state from the node config */
  stylesByElementState?: StylesByElementState;
  /** The element state key for the currently edited state (if any) */
  elementStateKey: ElementStateKey | null;
  /** The parent flex direction to determine orientation */
  parentFlexDirection?: string;
  /** CSS variables extracted from bindings */
  cssVariables: CSSVariables;
  /** Whether fit-content sizing should be enabled (typically true if container has content) */
  enableFitContent: boolean;
  /** Optional additional transform to apply after layout transform (e.g., alignment for FlexboxContainer) */
  additionalTransform?: (
    cssProperties: Partial<CSSProperties>,
    layoutSettings: LayoutSettings,
  ) => Partial<CSSProperties>;
  /** Optional background CSS to apply */
  background?: {
    value: string;
    blendMode?: string;
  };
};

export type DetailViewCssResult = {
  /** The final flattened CSS properties ready for rendering */
  cssProperties: CSSProperties & CSSVariables;
  /** The merged layout settings (base + element state overrides) */
  mergedLayoutSettings: LayoutSettings;
  /** The merged CSS properties before layout transform (base + element state overrides) */
  mergedCssProperties: Partial<CSSProperties>;
};

/**
 * Pure function that generates flattened CSS properties for a details view container
 * based on config, element state, and parent context.
 *
 * This function handles the complete CSS generation pipeline:
 * 1. Determines parent orientation from flex direction
 * 2. Merges layout settings from element state if editing a non-default state
 * 3. Merges CSS properties from element state
 * 4. Applies CSS variables from bindings
 * 5. Transforms layout settings to CSS (flex properties, width/height based on behavior)
 * 6. Applies optional additional transforms (e.g., alignment)
 * 7. Applies background CSS if provided
 * 8. Applies explicit CSS overrides last so they take precedence
 *
 * @returns The final CSS properties and merged values for use in rendering and DimensionsCalculator
 */
export const generateDetailViewCss = ({
  baseLayoutSettings,
  baseCssProperties,
  stylesByElementState,
  elementStateKey,
  parentFlexDirection,
  cssVariables,
  enableFitContent,
  additionalTransform,
  background,
}: GenerateDetailViewCssParams): DetailViewCssResult => {
  // Determine parent orientation from flex direction
  const parentOrientation: Orientation = ["row", "row-reverse"].includes(parentFlexDirection || "row")
    ? "horizontal"
    : "vertical";

  // Get element state config if editing a non-default state
  const elementStateConfig: ElementStateConfig | undefined = elementStateKey
    ? stylesByElementState?.[elementStateKey]
    : undefined;

  // Merge layout settings from element state
  const mergedLayoutSettings = elementStateConfig?.layoutSettings
    ? { ...baseLayoutSettings, ...elementStateConfig.layoutSettings }
    : baseLayoutSettings;

  // Merge CSS properties from element state
  const mergedCssProperties = combineCssWithOverrides(baseCssProperties ?? {}, elementStateConfig?.cssProperties);

  // Start with CSS variables
  let cssProperties: CSSProperties & CSSVariables = { ...cssVariables };

  // Apply layout settings transform
  cssProperties = {
    ...cssProperties,
    ...transformLayoutSettingsToCss(mergedCssProperties, mergedLayoutSettings, parentOrientation, enableFitContent),
  };

  // Apply additional transform if provided (e.g., alignment for FlexboxContainer)
  if (additionalTransform) {
    cssProperties = additionalTransform(cssProperties, mergedLayoutSettings);
  }

  // Apply background if provided
  if (background?.value) {
    cssProperties.background = background.value;
    if (background.blendMode) {
      cssProperties.backgroundBlendMode = background.blendMode;
    }

    // Combine background with backgroundColor if both exist
    if (cssProperties.backgroundColor) {
      cssProperties.background = `${background.value}, ${cssProperties.backgroundColor}`;
      cssProperties.backgroundColor = undefined;
    }
  }

  // Apply explicit CSS overrides, but exclude layout-derived properties when their behavior is not "fixed".
  // When horizontalBehavior is "fill" or "fit", the width should come from layout settings, not from explicit CSS.
  // When verticalBehavior is "fill" or "fit", the height should come from layout settings, not from explicit CSS.
  const explicitOverrides = { ...mergedCssProperties };

  // Remove layout-derived properties when they should be controlled by layout behavior
  if (mergedLayoutSettings.horizontalBehavior !== "fixed") {
    delete explicitOverrides.width;
  }
  if (mergedLayoutSettings.verticalBehavior !== "fixed") {
    delete explicitOverrides.height;
  }

  // Remove offset-origin-derived margin/inset properties so explicit overrides don't overwrite centering CSS
  if (mergedLayoutSettings.offsetOrigin) {
    const originConfig = getOffsetOriginConfig(mergedLayoutSettings.offsetOrigin);
    if (originConfig.horizontalCenter) {
      delete explicitOverrides.marginLeft;
      delete explicitOverrides.marginRight;
      delete explicitOverrides.left;
      delete explicitOverrides.right;
    }
    if (originConfig.verticalCenter) {
      delete explicitOverrides.marginTop;
      delete explicitOverrides.marginBottom;
      delete explicitOverrides.top;
      delete explicitOverrides.bottom;
    }
  }

  cssProperties = { ...cssProperties, ...explicitOverrides };

  return {
    cssProperties,
    mergedLayoutSettings,
    mergedCssProperties,
  };
};

export type UseDetailViewCssParams = {
  /** The node ID */
  nodeId: number;
  /** Ancestor IDs from parent context */
  ancestorIds: number[] | undefined;
  /** The base layout settings from the node config */
  baseLayoutSettings: LayoutSettings;
  /** The base CSS properties from the node config */
  baseCssProperties: Partial<CSSProperties> | undefined;
  /** The styles by element state from the node config */
  stylesByElementState: StylesByElementState | undefined;
  /** Node references for element state key resolution */
  nodeReferences: Record<string, number> | undefined;
  /** The parent flex direction to determine orientation */
  parentFlexDirection: string | undefined;
  /** CSS binding nodes to extract variables from */
  cssBindings: DesignerNode[];
  /** Whether fit-content sizing should be enabled (typically true if container has content) */
  enableFitContent: boolean;
  /** Optional additional transform to apply after layout transform (e.g., alignment for FlexboxContainer) */
  additionalTransform?: (cssProperties: CSSProperties, layoutSettings: LayoutSettings) => CSSProperties;
  /** Optional background CSS to apply */
  background?: {
    value: string;
    blendMode?: string;
  };
};

/**
 * Hook that generates flattened CSS properties for a details view container
 * based on config, element state, and parent context.
 *
 * @see generateDetailViewCss for the core logic
 */
export const useDetailViewCss = ({
  nodeId,
  ancestorIds,
  baseLayoutSettings,
  baseCssProperties,
  stylesByElementState,
  nodeReferences,
  parentFlexDirection,
  cssBindings,
  enableFitContent,
  additionalTransform,
  background,
}: UseDetailViewCssParams): DetailViewCssResult => {
  // Resolve the element state key for the currently edited state
  const elementStateKey = useDetailViewElementStateKey(nodeId, ancestorIds, nodeReferences);

  // Extract CSS variables from bindings
  const cssVariables = useMemo(
    () => Object.fromEntries(cssBindings.map((b) => [b.config.name ?? "", b.config.placeholder ?? 0])) as CSSVariables,
    [cssBindings],
  );

  // Generate the CSS properties
  return useMemo(
    () =>
      generateDetailViewCss({
        baseLayoutSettings,
        baseCssProperties,
        stylesByElementState,
        elementStateKey,
        parentFlexDirection,
        cssVariables,
        enableFitContent,
        additionalTransform,
        background,
      }),
    [
      elementStateKey,
      cssVariables,
      parentFlexDirection,
      stylesByElementState,
      baseLayoutSettings,
      baseCssProperties,
      enableFitContent,
      additionalTransform,
      background,
    ],
  );
};
